import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const SubdomainContext = createContext(null);

export const useSubdomain = () => {
  const context = useContext(SubdomainContext);
  if (!context) {
    throw new Error('useSubdomain must be used within a SubdomainProvider');
  }
  return context;
};

export const SubdomainProvider = ({ children }) => {
  const [subdomain, setSubdomain] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMainDomain, setIsMainDomain] = useState(true);

  useEffect(() => {
    detectSubdomain();
  }, []);

  const detectSubdomain = async () => {
    try {
      const hostname = window.location.hostname;
      
      // Check if we're on a subdomain
      // Patterns: subdomain.trados.app, subdomain.localhost, etc.
      let detectedSubdomain = null;
      
      if (hostname.endsWith('.trados.app')) {
        // Production: company.trados.app
        const parts = hostname.split('.trados.app')[0];
        if (parts && !['www', 'admin', 'api', 'app'].includes(parts.toLowerCase())) {
          detectedSubdomain = parts.toLowerCase();
        }
      } else if (hostname.includes('.localhost') || hostname.includes('.local')) {
        // Development: company.localhost
        const parts = hostname.split('.')[0];
        if (parts && parts !== 'localhost' && parts !== 'www') {
          detectedSubdomain = parts.toLowerCase();
        }
      }

      setSubdomain(detectedSubdomain);
      setIsMainDomain(!detectedSubdomain);

      // If we have a subdomain, fetch company info
      if (detectedSubdomain) {
        await fetchCompanyBySubdomain(detectedSubdomain);
      }
    } catch (error) {
      console.error('Error detecting subdomain:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyBySubdomain = async (sub) => {
    try {
      const response = await api.get(`/companies/by-subdomain/${sub}`);
      if (response.data) {
        setCompany(response.data);
        // Set company ID header for subsequent API calls
        api.defaults.headers.common['X-Company-ID'] = response.data.id;
      }
    } catch (error) {
      console.error('Company not found for subdomain:', sub);
      setCompany(null);
    }
  };

  const value = {
    subdomain,
    company,
    loading,
    isMainDomain,
    isSubdomain: !isMainDomain,
    refreshCompany: () => subdomain && fetchCompanyBySubdomain(subdomain)
  };

  return (
    <SubdomainContext.Provider value={value}>
      {children}
    </SubdomainContext.Provider>
  );
};

export default SubdomainContext;
